import React, { useEffect, useState } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { PRODUCTS_BY_SLUG } from '../data/products';
import { useGlobalWaLink } from '../contexts/SettingsContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductPage = () => {
  const WA_LINK = useGlobalWaLink();
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').split('/')[0];
  const product = PRODUCTS_BY_SLUG[slug];
  const [audience, setAudience] = useState('individual');
  const [testimonials, setTestimonials] = useState([]);
  const [cases, setCases] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!product) return;
    const fetchAll = async () => {
      try {
        const [t, c, e] = await Promise.all([
          axios.get(`${API}/public/testimonials?product_id=${product.slug}`),
          axios.get(`${API}/public/cases?product_id=${product.slug}`),
          axios.get(`${API}/public/events?product_id=${product.slug}`),
        ]);
        setTestimonials(t.data || []);
        setCases(c.data || []);
        setEvents(e.data || []);
      } catch (err) {
        console.warn('Failed to load product content:', err?.message || err);
      }
    };
    fetchAll();
    window.scrollTo(0, 0);
  }, [product]);

  if (!product) return <Navigate to="/" replace />;

  const format = audience === 'individual' ? product.format_individual : product.format_corporate;
  const longText = audience === 'individual' ? product.long_individual : product.long_corporate;
  const tagline = audience === 'individual' ? product.tagline_individual : product.tagline_corporate;

  return (
    <>
      <SEO
        title={`${product.name} — Instituto Eneagrama`}
        description={product.tagline_individual}
        path={`/${product.slug}`}
      />
      <Header />
      <main className="min-h-screen">
        {/* HERO */}
        <section
          className="relative pt-32 pb-20 border-b border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.65)' }}
          data-testid="product-hero"
        >
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1F1F1F] border border-[#333333] text-accent-orange mb-6">
              <iconify-icon icon={product.icon} className="text-4xl"></iconify-icon>
            </div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
              Programa IE
            </span>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 text-[#EAEAEA]">
              {product.name}
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 leading-relaxed">{tagline}</p>

            {/* Audience tabs */}
            <div className="flex justify-center mt-10">
              <div className="bg-[#1A1A1A] border border-[#333333] p-1 rounded-full inline-flex">
                <button
                  type="button"
                  onClick={() => setAudience('individual')}
                  data-testid="product-tab-individual"
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                    audience === 'individual'
                      ? 'bg-accent-orange text-white'
                      : 'text-neutral-400 hover:text-accent-orange'
                  }`}
                >
                  Pessoa Física
                </button>
                <button
                  type="button"
                  onClick={() => setAudience('corporate')}
                  data-testid="product-tab-corporate"
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                    audience === 'corporate'
                      ? 'bg-accent-orange text-white'
                      : 'text-neutral-400 hover:text-accent-orange'
                  }`}
                >
                  In Company
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* SOBRE O PROGRAMA */}
        <section className="relative py-20" style={{ background: 'rgba(17, 17, 17, 0.78)' }}>
          <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-7">
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                  Sobre o programa
                </span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#EAEAEA]">
                  {tagline}
                </h2>
                <p className="text-base text-neutral-300 leading-relaxed whitespace-pre-line">
                  {longText}
                </p>
              </div>

              <aside className="md:col-span-5">
                <div className="bg-[#0F0F0F] border border-[#333333] rounded-xl p-6 sticky top-24">
                  <div className="text-xs font-mono uppercase tracking-widest text-accent-orange mb-1">
                    {format.modality}
                  </div>
                  <div className="text-lg font-semibold text-[#EAEAEA] mb-4">{format.duration}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">
                    O que está incluso
                  </div>
                  <ul className="space-y-2">
                    {format.included.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-neutral-300">
                        <iconify-icon
                          icon="solar:check-circle-linear"
                          className="text-accent-orange text-base mt-0.5 flex-shrink-0"
                        ></iconify-icon>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* COMPETÊNCIAS */}
        <section className="relative py-20" style={{ background: 'rgba(10, 10, 10, 0.72)' }}>
          <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-12">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Competências desenvolvidas
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EAEAEA]">
                O que você ganha ao completar o programa
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {product.competencies.map((c) => (
                <div
                  key={c.title}
                  className="bg-[#1A1A1A] border border-[#333333] rounded-xl p-5 flex items-start gap-4 hover:border-accent-orange transition-colors"
                  data-testid={`competency-${c.title}`}
                >
                  <div className="w-11 h-11 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange flex-shrink-0">
                    <iconify-icon icon={c.icon} className="text-xl"></iconify-icon>
                  </div>
                  <div className="text-sm font-semibold text-[#EAEAEA] leading-tight">
                    {c.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DEPOIMENTOS */}
        <section className="relative py-20" style={{ background: 'rgba(17, 17, 17, 0.78)' }}>
          <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-10">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Depoimentos
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EAEAEA]">
                Depoimentos de eneagramados
              </h2>
            </div>
            {testimonials.length === 0 ? (
              <div className="text-center text-sm text-neutral-500 bg-[#1A1A1A] border border-[#333333] rounded-xl py-10">
                Em breve depoimentos específicos deste programa.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {testimonials.map((t) => (
                  <div
                    key={t.testimonial_id}
                    className="bg-[#1A1A1A] border border-[#333333] rounded-xl p-5 hover:border-accent-orange transition-colors"
                    data-testid={`product-testimonial-${t.testimonial_id}`}
                  >
                    {t.image && (
                      <div className="aspect-square mb-4 rounded-lg overflow-hidden bg-neutral-900">
                        <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="font-semibold text-[#EAEAEA] mb-1">{t.name}</div>
                    <div className="text-xs text-neutral-500 mb-3">
                      {t.company}
                      {t.role && ` · ${t.role}`}
                    </div>
                    <p className="text-sm text-neutral-400 leading-relaxed mb-3">
                      &ldquo;{t.text}&rdquo;
                    </p>
                    {t.video_url && (
                      <a
                        href={t.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-accent-orange"
                      >
                        Assistir
                        <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CASES */}
        <section className="relative py-20" style={{ background: 'rgba(10, 10, 10, 0.72)' }}>
          <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-10">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Resultados reais
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EAEAEA]">Cases do programa</h2>
            </div>
            {cases.length === 0 ? (
              <div className="text-center text-sm text-neutral-500 bg-[#1A1A1A] border border-[#333333] rounded-xl py-10">
                Em breve cases específicos deste programa.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {cases.map((c) => (
                  <article
                    key={c.case_id}
                    className="bg-[#1A1A1A] border border-[#333333] rounded-xl overflow-hidden hover:border-accent-orange transition-colors"
                    data-testid={`product-case-${c.case_id}`}
                  >
                    {c.image && (
                      <div className="aspect-video bg-neutral-900 overflow-hidden">
                        <img src={c.image} alt={c.company} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="text-xs text-accent-orange font-mono uppercase tracking-widest mb-2">
                        {c.state || c.franchise_name}
                      </div>
                      <div className="font-semibold text-[#EAEAEA] mb-2">{c.company}</div>
                      <p className="text-sm text-neutral-400 leading-relaxed">{c.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* AGENDA */}
        <section className="relative py-20" style={{ background: 'rgba(17, 17, 17, 0.78)' }}>
          <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-10">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Agenda
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#EAEAEA] mb-4">
                Próximas turmas
              </h2>
              <p className="text-base text-neutral-400">
                Selecione uma turma ou fale com um especialista para orientação personalizada.
              </p>
            </div>
            {events.length === 0 ? (
              <div className="text-center text-sm text-neutral-500 bg-[#1A1A1A] border border-[#333333] rounded-xl py-10 mb-10">
                Sem turmas abertas no momento — fale com a equipe para próximas turmas.
              </div>
            ) : (
              <div className="space-y-3 mb-10">
                {events.slice(0, 6).map((e) => (
                  <div
                    key={e.event_id}
                    className="bg-[#1A1A1A] border border-[#333333] rounded-xl p-5 flex flex-wrap items-center justify-between gap-4 hover:border-accent-orange transition-colors"
                    data-testid={`product-event-${e.event_id}`}
                  >
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-accent-orange">
                        {new Date(e.event_date).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}{' '}
                        · {e.event_time}
                      </div>
                      <div className="font-semibold text-[#EAEAEA] mt-1">{e.title}</div>
                      <div className="text-xs text-neutral-500">
                        {e.location} · {e.event_type}
                      </div>
                    </div>
                    <a
                      href={WA_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold uppercase tracking-wider text-accent-orange hover:text-[#E55D00]"
                    >
                      Reservar →
                    </a>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-cta-diagnosis"
                className="px-8 py-4 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors flex items-center justify-center gap-2"
              >
                <iconify-icon icon="solar:calendar-mark-linear" className="text-lg"></iconify-icon>
                Agende seu Diagnóstico
              </a>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-cta-specialist"
                className="px-8 py-4 border border-[#333333] text-[#EAEAEA] rounded-full text-sm font-bold uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors flex items-center justify-center gap-2"
              >
                <iconify-icon icon="solar:phone-linear" className="text-lg"></iconify-icon>
                Falar com Especialista
              </a>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section
          className="relative py-20 border-t border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#EAEAEA] mb-4">
              Assuma a governança do seu padrão
            </h2>
            <p className="text-base text-neutral-400 mb-8">
              Fale com a equipe técnica do Instituto Eneagrama.
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-cta-final"
              className="inline-flex items-center gap-2 px-10 py-4 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
            >
              <iconify-icon icon="solar:chat-round-line-linear" className="text-lg"></iconify-icon>
              Fale com a equipe técnica
            </a>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                Outros programas:
              </span>
              {Object.values(PRODUCTS_BY_SLUG)
                .filter((p) => p.slug !== product.slug)
                .map((p) => (
                  <Link
                    key={p.slug}
                    to={`/${p.slug}`}
                    className="text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-accent-orange border-b border-transparent hover:border-accent-orange pb-0.5 transition-colors"
                  >
                    {p.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ProductPage;
